<template>
  <div>
    <button class="flex-y-center gap-2 group cursor-auto">
      <span class="text-[28px] leading-130 font-semibold text-dark">
        {{ $t("enter_code") }}
      </span>
    </button>
    <p class="text-sm leading-130 font-normal text-gray-500 mt-4">
      {{ $t("we_send_code") }}
    </p>
    <button
      class="flex-y-center px-2 py-1.5 gap-2.5 rounded bg-gray-200 group mt-2"
    >
      <span class="text-sm leading-130 font-normal text-dark-100">
        {{ email }}
      </span>
    </button>

    <SFormGroup :label="$t('confirm_code')" class="my-8">
      <COtp
        :key="resend + ''"
        v-model="form.values.code"
        :error="form.$v.value.code?.$error || otpError"
        @keypress.enter.prevent="onEnter"
      />
    </SFormGroup>
    <div
      v-if="!resend"
      :key="resend + ''"
      class="flex items-center justify-center gap-2 mt-4 mb-8"
    >
      <span class="text-sm text-gray">{{ $t("resend_code") }}:</span>
      <CTimer seconds="299" @timeout="timeout" />
    </div>
    <div
      v-else
      class="flex items-center justify-center gap-2 mt-4 mb-8 cursor-pointer"
      @click="onResend"
    >
      <span class="text-sm text-green">{{ $t("resend_code_f") }}</span>
      <img src="/images/svg/refresh.svg" alt="" />
    </div>
    <SButton
      class="w-full"
      :text="$t('confirm')"
      @click="submit"
      :loading="loading"
      v-bind="{ disabled }"
    />
  </div>
</template>

<script setup lang="ts">
import SFormGroup from "@/components/Form/FGroup.vue";
import COtp from "@/components/Form/FOtp.vue";
import { ref, watch } from "vue";
import { useForm } from "@/composables/useForm";
import { minLength, required } from "@vuelidate/validators";
import SButton from "@/components/Common/CButton.vue";
import { useCustomToast } from "@/composables/useCustomToast";
import { useI18n } from "vue-i18n";
import CTimer from "@/components/Common/CTimer.vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { useRouter } from "vue-router";
import { JwtService } from "@/services/JwtService";
import apiService from "@/services/ApiService";

interface Props {
  email?: string;
}
const props = defineProps<Props>();

const store = useAuthStore();
const router = useRouter();

const emit = defineEmits(["submit", "back", "on-resend", "on-block"]);

const { showToast } = useCustomToast();
const { t } = useI18n();

const disabled = ref(true);
const resend = ref(false);
const otpError = ref(false);
const loading = ref(false);
const form = useForm(
  {
    code: "",
  },
  {
    code: { required, minLength: minLength(6) },
  },
);
function onResend() {
  resend.value = false;
  form.values.code = "";
  form.$v.value.$reset();
  emit("on-resend");
}
function timeout() {
  resend.value = true;
  form.values.code = "";
  form.$v.value.$reset();
}
watch(
  () => form.values.code,
  () => {
    otpError.value = false;
  },
);
async function submit() {
  form.$v.value.$touch();

  if (form.$v.value.$invalid) {
    showToast(t("fill_code"), "error");
    return;
  }

  loading.value = true;

  try {
    // Verify email and save tokens
    await verifyEmail(form.values.code, props.email);

    // Notify user about successful email change
    showToast(t("your_email_has_been_successfully_changed"), "success");

    // Check email verification status and handle accordingly
    await handleEmailVerification();

    // Set API header, emit event, and navigate
    finalizeSubmit();
  } catch (err) {
    handleError(err);
  } finally {
    loading.value = false;
  }
}

async function verifyEmail(code, email) {
  const res = await store.userChangeEmailVerify(code, email);
  JwtService.saveToken(res.data.access);
  JwtService.saveRefreshToken(res.data.refresh);
  return res;
}

async function handleEmailVerification() {
  const userData = await store.fetchUserData();
  if (!userData?.is_email_verified) {
    await store.userSendEmailVerification();
    await store.fetchUserData(); // Ensure the data is refreshed
    showToast(t("verified_title"), "success");
  }
}

function finalizeSubmit() {
  apiService.setHeader();
  emit("submit");
  router.push({ name: "Settings" });
}

function handleError(err) {
  if (err?.response?.status === 429) {
    emit("on-block");
    store.blockedTime = +err?.response?.headers["retry-after"];
  } else if (err?.response?.status === 400) {
    const message = err?.response?.data?.otp_code?.[0]
      ? t("invalid_code")
      : t("some_error");
    showToast(message, "error");
  } else {
    showToast(
      err?.response?.data?.[0]?.error?.message || t("some_error"),
      "error",
    );
  }
  otpError.value = true;
}

watch(
  () => form.values.code,
  (val: string) => {
    otpError.value = false;
    form.$v.value.$reset();
    disabled.value = val?.length !== 6;
  },
);

function onEnter() {
  submit();
}
</script>
